/**
 * Player-facing experiment API helper.
 *
 * The ONLY player-facing experiment surface. It calls the casino resolve
 * endpoint, which returns a bare bucket string plus (for treatment) a small
 * compliance-checked copy payload. It never exposes the assignment tables,
 * other players' data, or anything about Cluck Bucks / purchases.
 *
 * All calls go through the shared apiClient (never fetch/DB directly); the
 * request interceptor injects the Supabase JWT, and player_id is taken from
 * that token server-side (never sent from the client).
 *
 * FAIL-SAFE: resolveFirstRun never throws. Any error (network, 5xx, auth,
 * malformed body) resolves to the control shape so the lobby is never blocked
 * and the treatment can never fire on a degraded response.
 */

import apiClient from "../utils/apiClient";

// The single control fallback shape. Returned on every non-treatment outcome
// so callers only ever branch on `variant === "treatment"`.
export const CONTROL_RESULT = Object.freeze({
  variant: "control",
  treatment: null,
});

/**
 * Resolve the first-run activation experiment for the current player.
 * @returns {Promise<{ variant: 'control'|'treatment', treatment: object|null }>}
 *   Always resolves; never rejects.
 */
export async function resolveFirstRun() {
  try {
    const res = await apiClient.post(
      "/api/casino/experiments/first-run-activation/resolve",
    );
    const data = res?.data;

    // Only a well-formed treatment payload with a game_code activates the
    // interstitial. Anything else degrades to control (fail-safe).
    if (
      data &&
      data.variant === "treatment" &&
      data.treatment &&
      typeof data.treatment.game_code === "string" &&
      data.treatment.game_code.length > 0
    ) {
      return { variant: "treatment", treatment: data.treatment };
    }

    return CONTROL_RESULT;
  } catch {
    // Swallow everything. The experiment must never surface an error to the
    // player or block the lobby; a failed resolve is simply "control".
    return CONTROL_RESULT;
  }
}

/**
 * Resolve a generic A/B experiment bucket for the current player.
 *
 * Calls the generic resolve endpoint (POST
 * /api/casino/experiments/:key/resolve), which returns a bare bucket string
 * `{ variant: 'control' | 'treatment' }`. player_id is taken server-side from
 * the JWT (never sent from the client), and the assignment is sticky per player
 * on the server.
 *
 * FAIL-SAFE: never throws and never returns anything other than the two known
 * buckets. Any error (network, 5xx, auth, malformed body, experiment not
 * running) resolves to `'control'` so the caller can never be blocked or fed a
 * bad bucket. Callers therefore only ever branch on `variant === 'treatment'`.
 *
 * @param {string} key - the experiment key (e.g. 'buy-prompt-style').
 * @returns {Promise<'control'|'treatment'>} Always resolves; never rejects.
 */
export async function resolveExperiment(key) {
  try {
    const res = await apiClient.post(
      `/api/casino/experiments/${encodeURIComponent(key)}/resolve`,
    );
    return res?.data?.variant === "treatment" ? "treatment" : "control";
  } catch {
    // Swallow everything. A failed resolve is simply the control bucket.
    return "control";
  }
}

export default { resolveFirstRun, resolveExperiment, CONTROL_RESULT };
